import { useEffect, useState } from 'react';
import { Event } from 'nostr-tools';
import { getNostrPool } from '../lib/nostrPool';
import { RELAYS } from '../config/constants';
import { profileCache, ProfileMetadata } from '../lib/profileCache';

interface ProfileState {
    metadata: ProfileMetadata | null;
    createdAt: number;
}

export const useNostrProfile = (pubkey: string | null) => {
    // 1. Stable Pool Memo
    const pool = getNostrPool();

    // 2. Combined Metadata State (Atomic)
    // Initialize from cache if available
    const [state, setState] = useState<ProfileState>(() => {
        const cached = pubkey ? profileCache.get(pubkey) : null;
        return {
            metadata: cached,
            createdAt: 0 // We don't strictly need the timestamp from cache for initial render
        };
    });

    // 3. Loading State
    const [isLoading, setIsLoading] = useState(!state.metadata);

    // 4. Subscription Effect
    useEffect(() => {
        if (!pubkey) {
            setState({ metadata: null, createdAt: 0 });
            return;
        }

        // Only show loading if we don't have cached data
        const cached = profileCache.get(pubkey);
        if (cached) {
            setState({ metadata: cached, createdAt: 0 });
            setIsLoading(false);
        } else {
            setIsLoading(true);
            setState({ metadata: null, createdAt: 0 });
        }

        // Safety timeout: stop loading after 5 seconds even if relays are slow
        const timeout = setTimeout(() => {
            setIsLoading(false);
        }, 5000);

        const sub = pool.subscribeMany(RELAYS, {
            kinds: [0],
            authors: [pubkey]
        }, {
            onevent(event: Event) {
                clearTimeout(timeout);
                setState(prev => {
                    if (event.created_at >= prev.createdAt) {
                        try {
                            const data = JSON.parse(event.content);
                            // Store in global cache
                            profileCache.set(pubkey, data);

                            return {
                                metadata: data,
                                createdAt: event.created_at
                            };
                        } catch (e) {
                            console.error('Failed to parse profile metadata:', e);
                        }
                    }
                    return prev;
                });
                setIsLoading(false);
            },
            oneose() {
                setIsLoading(false);
            }
        });

        return () => {
            clearTimeout(timeout);
            sub.close();
        };
    }, [pubkey, pool]);

    return { metadata: state.metadata, isLoading };
};
